import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsTab = ({ getAuthHeaders }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchTestimonials();
  }, [filter]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/admin/testimonials`
        : `${BACKEND_URL}/api/admin/testimonials?status=${filter}`;
      
      const response = await axios.get(url, {
        ...getAuthHeaders()
      });
      
      setTestimonials(response.data.testimonials || []);
      console.log('Testimonials loaded:', response.data.testimonials?.length || 0);
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
      alert('Failed to load testimonials: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/testimonials/${id}/approve`,
        {},
        {
          ...getAuthHeaders()
        }
      );
      alert('Testimonial approved successfully');
      fetchTestimonials();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/testimonials/${id}/reject`,
        {},
        {
          ...getAuthHeaders()
        }
      );
      alert('Testimonial rejected successfully');
      fetchTestimonials();
    } catch (err) {
      alert('Failed to reject: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/testimonials/${id}`,
        {
          ...getAuthHeaders()
        }
      );
      alert('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/testimonials/${id}/toggle-featured`,
        {},
        {
          ...getAuthHeaders()
        }
      );
      alert('Featured status updated');
      fetchTestimonials();
    } catch (err) {
      alert('Failed to toggle featured: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditClick = (testimonial) => {
    setEditingTestimonial(testimonial);
    setEditForm({
      customer_name: testimonial.customer_name,
      company_name: testimonial.company_name || '',
      email: testimonial.email,
      phone: testimonial.phone || '',
      service_type: testimonial.service_type || '',
      testimonial_text: testimonial.testimonial_text,
      rating: testimonial.rating
    });
  };

  const handleEditSubmit = async () => {
    if (!editingTestimonial) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/testimonials/${editingTestimonial.id}`,
        editForm,
        {
          ...getAuthHeaders()
        }
      );
      alert('Testimonial updated successfully');
      setEditingTestimonial(null);
      setEditForm({});
      fetchTestimonials();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingTestimonial(null);
    setEditForm({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const pendingCount = testimonials.filter(t => t.status === 'pending').length;
  const approvedCount = testimonials.filter(t => t.status === 'approved').length;
  const rejectedCount = testimonials.filter(t => t.status === 'rejected').length;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Testimonials Management</h2>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Pending Review</div>
          <div className="text-3xl font-bold text-yellow-600">{pendingCount}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Approved</div>
          <div className="text-3xl font-bold text-green-600">{approvedCount}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Rejected</div>
          <div className="text-3xl font-bold text-red-600">{rejectedCount}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">All Testimonials</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Testimonials List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading testimonials...</div>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-500">No testimonials found</div>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{testimonial.customer_name}</h3>
                  <p className="text-sm text-gray-600">{testimonial.company_name}</p>
                  <p className="text-sm text-gray-500">Service: {testimonial.service_type}</p>
                </div>
                <div className="text-right">
                  <div className="text-yellow-400 text-xl">
                    {'★'.repeat(testimonial.rating)}{'☆'.repeat(5 - testimonial.rating)}
                  </div>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                    testimonial.status === 'approved' ? 'bg-green-100 text-green-800' :
                    testimonial.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {testimonial.status}
                  </div>
                  {testimonial.is_featured && (
                    <div className="text-sm text-blue-600 font-medium mt-1">⭐ Featured on Homepage</div>
                  )}
                </div>
              </div>

              <p className="text-gray-700 mb-4 italic">"{testimonial.testimonial_text}"</p>

              <div className="text-sm text-gray-500 mb-4">
                <div>Email: {testimonial.email}</div>
                {testimonial.phone && <div>Phone: {testimonial.phone}</div>}
                <div>Submitted: {formatDate(testimonial.submitted_at)}</div>
                {testimonial.approved_at && <div>Approved: {formatDate(testimonial.approved_at)}</div>}
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {testimonial.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(testimonial.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(testimonial.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </>
                )}
                {testimonial.status === 'approved' && (
                  <button
                    onClick={() => handleToggleFeatured(testimonial.id)}
                    className={`px-4 py-2 rounded-lg ${
                      testimonial.is_featured
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {testimonial.is_featured ? 'Remove from Homepage' : 'Feature on Homepage'}
                  </button>
                )}
                <button
                  onClick={() => handleEditClick(testimonial)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingTestimonial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4">Edit Testimonial</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={editForm.customer_name}
                  onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                <input
                  type="text"
                  value={editForm.service_type}
                  onChange={(e) => setEditForm({...editForm, service_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial Text *</label>
                <textarea
                  value={editForm.testimonial_text}
                  onChange={(e) => setEditForm({...editForm, testimonial_text: e.target.value})}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                <select
                  value={editForm.rating}
                  onChange={(e) => setEditForm({...editForm, rating: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                >
                  <option value={5}>5 Stars</option>
                  <option value={4}>4 Stars</option>
                  <option value={3}>3 Stars</option>
                  <option value={2}>2 Stars</option>
                  <option value={1}>1 Star</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleEditSubmit}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsTab;
